import { Directive, HostListener, Input } from '@angular/core';
import { formatNumeric } from '../helper/format-numeric';

@Directive({
  selector: 'input[appNumeric]',
})
export class NumericDirective {
  @Input() numericType?: 'float' | 'number' | 'digitalString' | 'money' | 'decimal' | 'decimalString' | false;
  @Input() min = 0;
  @Input() max = 9007199254740991;
  // Максимальное число знаков после запятой
  @Input() precision = 2;
  // Минимальное число знаков после запятой
  @Input() minPrecision: number | null = null;
  @Input() shouldConvertToNegative = false;
  @Input() forbidZeroValue = false;
  @Input() textZeroValue = false;
  private initValue = '';

  private floatingPointTypes = ['float', 'money', 'decimal', 'decimalString'];

  /**
   * Событие фокуса
   */
  @HostListener('blur', ['$event.target'])
  onBlur(el: HTMLInputElement): void {
    const value = el.value;
    if (value === null || value === '') {
      return;
    }
    if (this.numericType && String(this.format(value)) !== String(value)) {
      el.value = this.format(value) as string;
      this.dispatchEvent(el, 'blur');
      this.dispatchEvent(el, 'input');
    }
  }

  /**
   * Событие инпута
   */
  @HostListener('input', ['$event.target', '$event.target.value'])
  onInput(el: HTMLInputElement, value: string): boolean {
    if (value === null || value === '' || (this.forbidZeroValue && !this.textZeroValue && +value === 0)) {
      el.value = '';
      this.initValue = '';

      return false;
    }

    if (value.length === 1 && value[0] === '-' && this.forbidZeroValue) {
      return false;
    }

    let selectionStart = el.selectionStart as number;
    let selectionEnd = el.selectionEnd as number;

    if (+value[0] === 0 && !this.textZeroValue && (this.numericType === 'money' || this.numericType === 'decimal')) {
      selectionStart = selectionEnd = selectionStart - 1;
    }

    // Если предыдущая строка была нулевым значением, то делаем замену первого нуля на введенное значение
    if (+this.initValue === 0 && value.length === +this.initValue.length + 1 && !(+value === 0) && this.numericType === 'money') {
      value = value.replace(/0/, '');
    }

    if (this.shouldConvertToNegative || this.textZeroValue) {
      if (value[0] !== '-') {
        if (!this.textZeroValue) {
          value = '-' + value;
        }
        if (value[0] === '-') {
          selectionStart = selectionEnd = selectionStart === 0 ? 1 : selectionStart + 1;
        }
      }

      if (+value === 0 && !this.textZeroValue && this.numericType === 'money') {
        value = value.slice(1);
        selectionStart = selectionEnd = 0;
      }
    }

    if (this.numericType && String(this.format(value, false)) !== String(value)) {
      // сохранение позиции курсора
      if (this.floatingPointTypes.indexOf(this.numericType) !== -1) {
        value = value.trim();
        const countStartWhitespace = value.split(' ').length - 1;
        value = this.formatValue(value);
        const countAdditionalWhitespace = value.split(' ').length - 1 - countStartWhitespace;
        el.value = value;
        el.selectionStart = selectionStart + countAdditionalWhitespace;
        el.selectionEnd = selectionEnd + countAdditionalWhitespace;
      } else {
        el.value = this.format(value) as string;
      }

      this.initValue = value;

      return this.dispatchEvent(el, 'input');
    }

    el.value = value;
    this.initValue = el.value;

    el.selectionStart = selectionStart;
    el.selectionEnd = selectionEnd;

    return true;
  }

  /**
   * Формат значения
   */
  private formatValue(value: string): string {
    if (this.initValue.split('.').join('') === value) {
      value = this.format(this.initValue, false) as string;
    } else {
      value = this.format(value, false) as string;
    }

    return value;
  }

  /**
   * Форматирует введенное значение в соответсвии с выбранным типом
   */
  private format(value: string, trimEndZeros = true): number | string {
    return formatNumeric(
      value,
      this.numericType as string,
      this.precision,
      trimEndZeros,
      this.shouldConvertToNegative || this.textZeroValue,
    );
  }

  /**
   * Отправит ивент
   */
  private dispatchEvent(el: HTMLInputElement, eventType: string): boolean {
    const event = document.createEvent('Event');
    event.initEvent(eventType, false, false);

    return el.dispatchEvent(event);
  }
}
